import {Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Catalog} from 'app/Model/Catalog/Catalog';
import {ImportContainer} from 'app/Model/dataProcess/importContainer';
import {CatalogService} from 'app/Services/CatalogManagement/catalog.service';
import {Router} from "@angular/router";
import DevExpress from "devextreme";
import {DxContextMenuComponent} from "devextreme-angular";
import {ImportContainerService} from "../../../../Services/DataProcess/import-container.service";
import {custom} from "devextreme/ui/dialog";
import {TranslateService} from "@ngx-translate/core";
import {JobService} from "../../../../Services/job.service";
import {LoginService} from "../../../../Services/login.service";
import {StartAction} from "../../../../Model/Dto/StartAction";
import {SystemService} from "../../../../Services/system.service";
import {SignalrService} from "../../../../Services/signalr.service";
import {Message} from "../../../../Model/System/Message";
import {BackgroundJobEvent} from "../../../../Model/Dto/BackgroundJobEvent";
import ContextMenuItem = DevExpress.ui.dxFileManager.ContextMenuItem;

@Component({
  selector: 'np-catalog-tab',
  templateUrl: './catalog-tab.component.html',
  styleUrls: ['./catalog-tab.component.css'],
  standalone: false
})
export class CatalogTabComponent implements OnInit, OnChanges {
  @Input() container: ImportContainer;
  @ViewChild('catalogContextMenu', {static: false}) catalogContextMenu: DxContextMenuComponent;
  @Output() inspectCatalog = new EventEmitter<{ container: ImportContainer; catalogId: string }>();

  catalogs: Catalog[] = [];

  compareCatalogId: string;
  compareCandidates: Catalog[] = [];
  isDragOver: boolean = false;
  isDragOverCompare: boolean = false;
  archiveCatalogs: { id: string; name: string }[] = [];
  activeCatalogItems: { id: string; name: string }[] = [];
  compareCatalogItems: { id: string; name: string }[] = [];
  isArchiveLoading: boolean = false;
  selectedCatalogId: string;
  importMimeVisible: boolean = false;
  isTemplate: boolean = false;
  contextMenuItems: ContextMenuItem[];

  isComparing: boolean = false;
  comparisonCounts: { neu: number; geaendert: number; geloescht: number };
  isDeletingCatalog: boolean = false;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private catalogService: CatalogService,
    private router: Router,
    public importContainerService: ImportContainerService,
    public translate: TranslateService,
    private jobService: JobService,
    private loginService: LoginService,
    private systemService: SystemService,
    private signalrService: SignalrService
  ) {
    this.contextMenuItems = [
      {
        id: 0,
        text: this.translate.instant("Katalog anzeigen..."),
        icon: 'glyphicon glyphicon-eye-open',
        beginGroup: true
      },
      {
        id: 1,
        text: this.translate.instant("Medien importieren..."),
        icon: 'glyphicon glyphicon-copy',
        beginGroup: true
      },
      {
        id: 2,
        text: this.translate.instant("Löschen..."),
        icon: 'glyphicon glyphicon-trash',
        beginGroup: true
      },
    ]
  }

  async ngOnInit(): Promise<void> {
    if (this.hasContainerCatalogIds()) {
      await this.loadArchiveCatalogNames();
    }

    this.catalogService.catalogNameUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ id, name }) => {
        this.updateLocalCatalogName(id, name);
      });

    this.signalrService.refreshCatalogList$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(async () => {
      if (!this.isDeletingCatalog && this.container?.id) {
        const customerId = this.catalogService.loginService.currentUser.customerId;
        const result = await this.importContainerService.get(this.container.id, customerId);
        if (result) {
          this.container = result;
        }
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['container']) {
      if (this.hasContainerCatalogIds()) {
        await this.loadArchiveCatalogNames();
      } else {
        this.archiveCatalogs = [];
        this.activeCatalogItems = [];
        this.compareCatalogItems = [];
        this.selectedCatalogId = null;
      }
    }
  }
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(): void {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    const droppedFiles: File[] = Array.from(event.dataTransfer?.files ?? []);
    this.handleSelectedFiles(droppedFiles);
  }

  async onDropAddTop(event: any): Promise<void> {
    const fromData = event.fromData;
    const fromIndex = event.fromIndex;

    if (!fromData || !Array.isArray(fromData) || !this.container) {
      return;
    }

    const droppedCatalog = fromData[fromIndex];
    if (!droppedCatalog?.id) {
      return;
    }

    if (this.activeCatalogItems.length > 0) {
      const prev = this.activeCatalogItems[0];
      if (prev.id !== droppedCatalog.id) {
        this.archiveCatalogs = [prev, ...this.archiveCatalogs];
      }
    }

    this.container.activeCatalogId = droppedCatalog.id;
    this.activeCatalogItems = [{ id: droppedCatalog.id, name: droppedCatalog.name }];

    this.archiveCatalogs = this.archiveCatalogs.filter(c => c.id !== droppedCatalog.id);
    if (this.compareCatalogItems.length > 0 && this.compareCatalogItems[0].id === droppedCatalog.id) {
      this.compareCatalogItems = [];
      this.container.compareCatalogId = null;
    }

    this.comparisonCounts = null;
    await this.persistDropZones();
  }

  onDragOverCompare(event: DragEvent): void {
    event.preventDefault();
    this.isDragOverCompare = true;
  }

  onDragLeaveCompare(): void {
    this.isDragOverCompare = false;
  }

  onDropCompare(event: DragEvent): void {
    event.preventDefault();
    this.isDragOverCompare = false;
  }

  async onDropAddCompare(event: any): Promise<void> {
    const fromData = event.fromData;
    const fromIndex = event.fromIndex;

    if (!fromData || !Array.isArray(fromData) || !this.container) {
      return;
    }

    const droppedCatalog = fromData[fromIndex];
    if (!droppedCatalog?.id) {
      return;
    }

    if (this.compareCatalogItems.length > 0) {
      const prev = this.compareCatalogItems[0];
      if (prev.id !== droppedCatalog.id) {
        this.archiveCatalogs = [prev, ...this.archiveCatalogs];
      }
    }

    this.container.compareCatalogId = droppedCatalog.id;
    this.compareCatalogItems = [{ id: droppedCatalog.id, name: droppedCatalog.name }];

    this.archiveCatalogs = this.archiveCatalogs.filter(c => c.id !== droppedCatalog.id);
    if (this.activeCatalogItems.length > 0 && this.activeCatalogItems[0].id === droppedCatalog.id) {
      this.activeCatalogItems = [];
      this.container.activeCatalogId = null;
    }

    this.comparisonCounts = null;
    await this.persistDropZones();
  }

  get canStartComparison(): boolean {
    return this.activeCatalogItems.length > 0 && this.compareCatalogItems.length > 0;
  }

  onStartComparison(): void {
    if (!this.canStartComparison || !this.container) {
      return;
    }

    this.isComparing = true;
    this.comparisonCounts = null;

    const startAction = new StartAction();
    startAction.customerId = this.loginService.currentUser.customerId;
    startAction.catalogId = this.container.activeCatalogId;
    startAction.actionId = `CompareCatalog(${this.container.compareCatalogId}:false:writeToSource)`;

    this.jobService.startUserAction(startAction).subscribe({
      next: (jobId) => {
        this.systemService.notify(new Message(this.translate.instant('Vergleich wurde gestartet.')));
        if (jobId) {
          this.subscribeToComparisonResult(jobId);
        } else {
          this.isComparing = false;
        }
      },
      error: () => {
        this.systemService.notify(new Message(this.translate.instant('Vergleich konnte nicht gestartet werden.')));
        this.isComparing = false;
      }
    });
  }

  private async subscribeToComparisonResult(jobId: number): Promise<void> {
    const jobIdString = jobId.toString();
    await this.signalrService.startFinishedJobFeed(jobIdString);

    this.signalrService.finishedJob$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({jobId: finishedId}) => {
      if (finishedId === jobIdString) {
        this.jobService.getJobEvents(jobId).subscribe({
          next: (events) => {
            const counts = this.parseComparisonEvents(events);
            this.comparisonCounts = counts;
            this.isComparing = false;
            this.signalrService.stopFinishedJobFeed(jobIdString);
          },
          error: () => {
            this.isComparing = false;
            this.signalrService.stopFinishedJobFeed(jobIdString);
          }
        });
      }
    });
  }

  private parseComparisonEvents(events: BackgroundJobEvent[]): { neu: number; geaendert: number; geloescht: number } | null {
    let neu: number;
    let geaendert: number;
    let geloescht: number;

    for (const event of events) {
      if (event.parameter?.startsWith('new:')) {
        neu = parseInt(event.parameter.split(':')[1], 10);
      } else if (event.parameter?.startsWith('changed:')) {
        geaendert = parseInt(event.parameter.split(':')[1], 10);
      } else if (event.parameter?.startsWith('deleted:')) {
        geloescht = parseInt(event.parameter.split(':')[1], 10);
      }
    }

    if (neu !== null && geaendert !== null && geloescht !== null) {
      return { neu, geaendert, geloescht };
    }
    return null;
  }

  onFileInputChange(event: Event): void {
    const fileInputElement = event.target as HTMLInputElement;
    const selectedFiles: File[] = Array.from(fileInputElement.files ?? []);

    this.handleSelectedFiles(selectedFiles);
    fileInputElement.value = '';
  }

  private handleSelectedFiles(selectedFiles: File[]): void {
    if (selectedFiles.length === 0) {
      return;
    }

    const readableList = selectedFiles.map(file => `${file.name} (${file.size} B)`);
    console.log('Import-Dateien:', readableList);
  }

  get hasCatalogs(): boolean {
    return (this.container?.catalogIds?.length ?? 0) > 0;
  }

  onCatalogOverflowClicked(catalogId: string | null | undefined, event: any): void {
    if (!catalogId || !this.container) {
      return;
    }
    this.catalogContextMenu.instance.option('target', event.event.currentTarget);
    this.catalogContextMenu.instance.option('position', {
      my: 'left top',
      at: 'left bottom',
      offset: '0 5'
    });
    this.catalogContextMenu.instance.show();
    this.selectCatalog(catalogId);
    this.inspectCatalog.emit({
      container: this.container,
      catalogId
    });
  }

  async itemClick(event: any): Promise<void> {
    switch (event.itemIndex) {
      case 0:
        this.router.navigate(['/catalog/' + this.catalogService.loginService.currentUser.customerId + '/' + this.selectedCatalogId + '/info']);
        break;
      case 1:
        this.importMimeVisible = true;
        break;
      case 2:
        this.showDeleteDialog(this.selectedCatalogId, this.catalogService.loginService.currentUser.customerId);
        break;
    }
  }

  hideImportMime() {
    this.importMimeVisible = false;
  }

  showDeleteDialog(deletedCatalogId: string, customerId: string) {
    let normalCatalogDialog = custom({
      showTitle: true,
      title: this.translate.instant("Katalog löschen"),
      dragEnabled: false,
      messageHtml: this.translate.instant("Wollen Sie den Katalog wirklich löschen?"),
      buttons: [
        {
          text: this.translate.instant("Ja"),
          onClick: async (e) => {
            await this.onDeleteClicked(deletedCatalogId, customerId);
          }
        },
        {
          text: this.translate.instant("Nein"),
        }
      ]
    });
    normalCatalogDialog.show()
  }

  async onDeleteClicked(catalogId: string, customerId: string) {
    this.archiveCatalogs = this.archiveCatalogs.filter(c => c.id !== catalogId);
    this.activeCatalogItems = this.activeCatalogItems.filter(c => c.id !== catalogId);
    this.compareCatalogItems = this.compareCatalogItems.filter(c => c.id !== catalogId);
    if (this.container.activeCatalogId === catalogId) {
      this.container.activeCatalogId = null;
    }
    if (this.container.compareCatalogId === catalogId) {
      this.container.compareCatalogId = null;
    }
    if (this.container.catalogIds) {
      this.container.catalogIds = this.container.catalogIds.filter(id => id !== catalogId);
    }
    this.selectedCatalogId = null;
    this.comparisonCounts = null;

    this.isDeletingCatalog = true;

    try {
      await Promise.all([
        this.importContainerService.deleteContainerCatalogsByCatalogId(catalogId, customerId),
        this.catalogService.remCatalogAsync(catalogId)
      ]);

      const result = await this.importContainerService.get(this.container.id, customerId);
      if (result) {
        this.container = result;
      }
    } finally {
      this.isDeletingCatalog = false;
    }
  }

  async onCatalogJumpClicked(catalogId: string | null | undefined): Promise<void> {
    if (!catalogId) {
      return;
    }

    this.catalogService.selectedCatalogId = catalogId;
    await this.router.navigate(['/catalog/' + this.catalogService.loginService.currentUser.customerId + '/' + catalogId + '/info']);
  }

  onCatalogRowClick(catalogId: string | null | undefined): void {
    if (!catalogId || !this.container) {
      return;
    }

    this.selectCatalog(catalogId);
    this.inspectCatalog.emit({
      container: this.container,
      catalogId
    });
  }

  async onDropAddArchive(event: any): Promise<void> {
    const fromData = event.fromData;
    const fromIndex = event.fromIndex;

    if (!fromData || !Array.isArray(fromData) || !this.container) {
      return;
    }

    const droppedCatalog = fromData[fromIndex];
    if (!droppedCatalog?.id) {
      return;
    }

    const toIndex = event.toIndex ?? 0;
    const updated = [...this.archiveCatalogs];
    updated.splice(toIndex, 0, { id: droppedCatalog.id, name: droppedCatalog.name });
    this.archiveCatalogs = updated;

    if (this.activeCatalogItems.some(c => c.id === droppedCatalog.id)) {
      this.activeCatalogItems = [];
      this.container.activeCatalogId = null;
    }
    if (this.compareCatalogItems.some(c => c.id === droppedCatalog.id)) {
      this.compareCatalogItems = [];
      this.container.compareCatalogId = null;
    }

    this.comparisonCounts = null;
    await this.persistDropZones();
  }

  onArchiveReorder(event: any): void {
    const fromIndex = event.fromIndex;
    const toIndex = event.toIndex;

    if (fromIndex === toIndex) {
      return;
    }

    const updated = [...this.archiveCatalogs];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    this.archiveCatalogs = updated;
  }

  private updateLocalCatalogName(catalogId: string, name: string): void {
    const active = this.activeCatalogItems.find(c => c.id === catalogId);
    if (active) active.name = name;

    const compare = this.compareCatalogItems.find(c => c.id === catalogId);
    if (compare) compare.name = name;

    const archive = this.archiveCatalogs.find(c => c.id === catalogId);
    if (archive) archive.name = name;
  }

  private selectCatalog(catalogId: string): void {
    this.selectedCatalogId = catalogId;
  }

  private hasContainerCatalogIds(): boolean {
    return !!this.container?.catalogIds && this.container.catalogIds.length > 0;
  }

  private async persistDropZones(): Promise<void> {
    if (!this.container?.id) {
      return;
    }

    const customerId = this.catalogService.loginService.currentUser.customerId;
    try {
      await this.importContainerService.updateDropZones(
        this.container.id,
        this.container.activeCatalogId ?? null,
        this.container.compareCatalogId ?? null,
        customerId
      );
    } catch (err) {
      console.error('Fehler beim Speichern der Drop-Zonen', err);
    }
  }

  private async loadArchiveCatalogNames(): Promise<void> {
    this.isArchiveLoading = true;
    this.archiveCatalogs = [];
    this.activeCatalogItems = [];
    this.compareCatalogItems = [];
    this.selectedCatalogId = null;

    try {
      const customerId = this.catalogService.loginService.currentUser.customerId;
      const catalogIds = this.container?.catalogIds ?? [];
      const uniqueIds = [...new Set(catalogIds)].filter(id => !!id);

      if (uniqueIds.length === 0) {
        return;
      }

      await this.catalogService.setCatalogs(customerId);

      const sortedIds = [...uniqueIds].reverse();

      const catalogInfos = await Promise.all(
        sortedIds.map(async id => {
          const fromList = this.catalogService.catalogs?.find(c => c.id === id);
          const name = fromList?.catalogName
            ?? await this.catalogService.getCatalogName(id, customerId);
          return { id, name };
        })
      );

      const activeId = this.container?.activeCatalogId ?? null;
      const compareId = this.container?.compareCatalogId ?? null;

      const activeCatalog = activeId ? catalogInfos.find(c => c.id === activeId) : undefined;
      this.activeCatalogItems = activeCatalog?.name ? [{ id: activeId, name: activeCatalog.name }] : [];

      const compareCatalog = compareId ? catalogInfos.find(c => c.id === compareId) : undefined;
      this.compareCatalogItems = compareCatalog?.name ? [{ id: compareId, name: compareCatalog.name }] : [];

      this.archiveCatalogs = catalogInfos
        .filter(c => c.id !== activeId && c.id !== compareId && !!c.name)
        .map(c => ({
          id: c.id,
          name: c.name as string
        }));
    } finally {
      this.isArchiveLoading = false;
    }
  }
}
